<script setup>
import DateTimePickerUnified from '@/components/Listing/DateTimePickerUnified.vue'
import { computed, ref } from 'vue'

definePage({
  meta: {
    layout: 'default',
    public: true,
  },
})

const selectedTime = ref('09:00')
const selectedDate = ref('2026-02-11')
const display = ref('list')
const layout = ref('list')
const format = ref('uk')
const stepMinutes = ref(30)
const startTime = ref('08:30')
const endTime = ref('20:30')
const fillHeight = ref(false)
const minHeight = ref(420)
const demoContainerHeight = ref('auto')
const density = ref('comfortable')
const componentWidth = ref('328')
const layoutVariant = ref('split')
const surfaceVariant = ref('transparent')
const controlsTab = ref('current')
const dpColor = ref('#1976D2')
const dpBgColor = ref('')
const dpBaseColor = ref('')
const dpElevation = ref(0)
const dpActive = ref(true)
const dpFlat = ref(false)
const dpHideHeader = ref(false)
const dpHideWeekdays = ref(false)
const dpLandscape = ref(false)
const dpMultiple = ref(false)
const dpShowAdjacentMonths = ref(true)
const dpFirstDayOfWeek = ref(1)
const dpViewMode = ref('month')
const dpTitle = ref('')
const dpHeader = ref('')
const dpWeekdayFormat = ref('short')
const dpMinDate = ref('')
const dpMaxDate = ref('')
const dpAllowedDatesMode = ref('all')

const preset = ref('none')

const presetDisabledSlots = computed(() => {
  if (preset.value === 'lunch')
    return ['12:00', '12:15', '12:30', '12:45', '13:00']

  if (preset.value === 'morning')
    return ['08:30', '08:45', '09:00', '09:15', '09:30', '09:45']

  if (preset.value === 'evening')
    return ['18:00', '18:15', '18:30', '18:45', '19:00', '19:15', '19:30', '19:45', '20:00', '20:15', '20:30']

  return []
})

const toDateOnly = value => {
  if (!value)
    return null

  // Already normalized string
  if (typeof value === 'string') {
    const directMatch = value.match(/^(\d{4}-\d{2}-\d{2})/)
    if (directMatch?.[1])
      return directMatch[1]
  }

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime()))
    return null

  // Normalize to local date to match what user selected in UI
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')

  return `${yyyy}-${mm}-${dd}`
}

const selectedDateOnly = computed(() => toDateOnly(selectedDate.value))

const isTimeDisabled = computed(() => {
  if (!selectedTime.value)
    return false

  return presetDisabledSlots.value.includes(selectedTime.value)
})

const combinedDateTimeLocal = computed(() => {
  if (!selectedDateOnly.value || !selectedTime.value || isTimeDisabled.value)
    return null

  return `${selectedDateOnly.value}T${selectedTime.value}`
})

const toLaravelDateTimeWithOffset = localValue => {
  if (!localValue) return null

  const match = String(localValue).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/)
  if (!match) return null

  const [, y, m, d, hh, mm] = match
  const date = new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), 0)
  if (Number.isNaN(date.getTime())) return null

  const tzOffsetMinutes = -date.getTimezoneOffset()
  const sign = tzOffsetMinutes >= 0 ? '+' : '-'
  const abs = Math.abs(tzOffsetMinutes)
  const tzH = String(Math.floor(abs / 60)).padStart(2, '0')
  const tzM = String(abs % 60).padStart(2, '0')

  return `${y}-${m}-${d}T${hh}:${mm}:00${sign}${tzH}:${tzM}`
}

const combinedDateTimeIso = computed(() => {
  if (!combinedDateTimeLocal.value)
    return null

  const date = new Date(combinedDateTimeLocal.value)
  if (Number.isNaN(date.getTime()))
    return null

  return date.toISOString()
})

const combinedDateTimeLaravel = computed(() =>
  toLaravelDateTimeWithOffset(combinedDateTimeLocal.value),
)

const combinedDateTimeFormattedUk = computed(() => {
  if (!combinedDateTimeLocal.value)
    return null

  const date = new Date(combinedDateTimeLocal.value)
  if (Number.isNaN(date.getTime()))
    return null

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(date)
})

const combinedDateTimeFormattedEn = computed(() => {
  if (!combinedDateTimeLocal.value)
    return null

  const date = new Date(combinedDateTimeLocal.value)
  if (Number.isNaN(date.getTime()))
    return null

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(date)
})

const simulationPayload = computed(() => ({
  selectedDateRaw: selectedDate.value,
  selectedDateOnly: selectedDateOnly.value,
  scheduledAtLocal: combinedDateTimeLocal.value,
  scheduledAtIso: combinedDateTimeIso.value,
  scheduledAtLaravel: combinedDateTimeLaravel.value,
  formattedUk: combinedDateTimeFormattedUk.value,
  formattedEn: combinedDateTimeFormattedEn.value,
  isValid: Boolean(combinedDateTimeLocal.value && combinedDateTimeIso.value),
}))

const configSnapshot = computed(() => ({
  selectedDate: selectedDate.value,
  selectedTime: selectedTime.value,
  display: display.value,
  layout: layout.value,
  format: format.value,
  stepMinutes: stepMinutes.value,
  startTime: startTime.value,
  endTime: endTime.value,
  fillHeight: fillHeight.value,
  minHeight: minHeight.value,
  demoContainerHeight: demoContainerHeight.value,
  density: density.value,
  layoutVariant: layoutVariant.value,
  surfaceVariant: surfaceVariant.value,
  datePicker: datePickerAttrs.value,
  disabledSlots: presetDisabledSlots.value,
}))

const allowedDateModes = [
  { title: 'All dates', value: 'all' },
  { title: 'Only weekdays', value: 'weekdays' },
  { title: 'Only weekends', value: 'weekends' },
  { title: 'Exclude Sundays', value: 'exclude-sunday' },
  { title: 'Exclude day 15', value: 'exclude-15' },
]

const allowedDatesFn = computed(() => {
  if (dpAllowedDatesMode.value === 'all')
    return undefined

  return value => {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime()))
      return false

    const day = date.getDay()
    const dateNumber = date.getDate()

    if (dpAllowedDatesMode.value === 'weekdays')
      return day >= 1 && day <= 5

    if (dpAllowedDatesMode.value === 'weekends')
      return day === 0 || day === 6

    if (dpAllowedDatesMode.value === 'exclude-sunday')
      return day !== 0

    if (dpAllowedDatesMode.value === 'exclude-15')
      return dateNumber !== 15

    return true
  }
})

const datePickerAttrs = computed(() => ({
  color: dpColor.value,
  bgColor: dpBgColor.value || undefined,
  baseColor: dpBaseColor.value || undefined,
  width: componentWidth.value || undefined,
  active: dpActive.value,
  flat: dpFlat.value,
  hideHeader: dpHideHeader.value,
  hideWeekdays: dpHideWeekdays.value,
  landscape: dpLandscape.value,
  multiple: dpMultiple.value,
  showAdjacentMonths: dpShowAdjacentMonths.value,
  firstDayOfWeek: dpFirstDayOfWeek.value,
  viewMode: dpViewMode.value,
  title: dpTitle.value || undefined,
  header: dpHeader.value || undefined,
  weekdayFormat: dpWeekdayFormat.value,
  allowedDates: allowedDatesFn.value,
  min: dpMinDate.value || undefined,
  max: dpMaxDate.value || undefined,
}))

const resetPreferences = () => {
  selectedDate.value = '2026-02-11'
  selectedTime.value = '09:00'
  display.value = 'list'
  layout.value = 'list'
  format.value = 'uk'
  stepMinutes.value = 30
  startTime.value = '08:30'
  endTime.value = '20:30'
  fillHeight.value = false
  minHeight.value = 420
  demoContainerHeight.value = 'auto'
  density.value = 'comfortable'
  componentWidth.value = '328'
  layoutVariant.value = 'split'
  surfaceVariant.value = 'transparent'
  controlsTab.value = 'current'
  dpColor.value = '#1976D2'
  dpBgColor.value = ''
  dpBaseColor.value = ''
  dpElevation.value = 0
  dpActive.value = true
  dpFlat.value = false
  dpHideHeader.value = false
  dpHideWeekdays.value = false
  dpLandscape.value = false
  dpMultiple.value = false
  dpShowAdjacentMonths.value = true
  dpFirstDayOfWeek.value = 1
  dpViewMode.value = 'month'
  dpTitle.value = ''
  dpHeader.value = ''
  dpWeekdayFormat.value = 'short'
  dpMinDate.value = ''
  dpMaxDate.value = ''
  dpAllowedDatesMode.value = 'all'
  preset.value = 'none'
}
</script>

<template>
  <VContainer
    class="py-6"
    fluid
  >
    <VCard
      class="mb-6"
      variant="outlined"
    >
      <VCardTitle class="text-h5">
        Time Slot Picker - Demo Isolata
      </VCardTitle>
      <VCardText>
        Usa questa pagina per testare tutte le modalità del componente senza impattare la form listing.
      </VCardText>
    </VCard>

    <VRow>
      <VCol
        cols="12"
        md="4"
      >
        <VCard
          class="mb-4 control-card"
          variant="outlined"
        >
          <VCardTitle class="text-subtitle-1">
            Controlli
          </VCardTitle>
          <VCardText class="control-scroll">
            <VTabs
              v-model="controlsTab"
              density="compact"
              class="mb-3"
            >
              <VTab value="current">
                Attuali
              </VTab>
              <VTab value="datepicker">
                VDatePicker
              </VTab>
            </VTabs>

            <VBtn
              color="primary"
              variant="tonal"
              block
              class="mb-3"
              @click="resetPreferences"
            >
              Reset demo preferences
            </VBtn>

            <VWindow v-model="controlsTab">
              <VWindowItem value="current">
                <VSelect
                  v-model="display"
                  density="comfortable"
                  hide-details="auto"
                  label="Display"
                  :items="[
                    { title: 'Select', value: 'select' },
                    { title: 'List', value: 'list' }
                  ]"
                  item-title="title"
                  item-value="value"
                  class="mb-3"
                />
                <VSelect
                  v-model="layout"
                  density="comfortable"
                  hide-details="auto"
                  label="Layout (solo list)"
                  :items="[
                    { title: 'List', value: 'list' },
                    { title: 'Grid', value: 'grid' }
                  ]"
                  item-title="title"
                  item-value="value"
                  :disabled="display !== 'list'"
                  class="mb-3"
                />
                <VSelect
                  v-model="format"
                  density="comfortable"
                  hide-details="auto"
                  label="Formato orario"
                  :items="[
                    { title: 'UK (AM/PM)', value: 'uk' },
                    { title: '24-hour', value: '24-hour' }
                  ]"
                  item-title="title"
                  item-value="value"
                  class="mb-3"
                />
                <VSelect
                  v-model="stepMinutes"
                  density="comfortable"
                  hide-details="auto"
                  label="Step minuti"
                  :items="[
                    { title: '15', value: 15 },
                    { title: '30', value: 30 },
                    { title: '60', value: 60 }
                  ]"
                  item-title="title"
                  item-value="value"
                  class="mb-3"
                />
                <VTextField
                  v-model="startTime"
                  density="comfortable"
                  hide-details="auto"
                  label="Ora inizio"
                  placeholder="08:30"
                  class="mb-3"
                />
                <VTextField
                  v-model="endTime"
                  density="comfortable"
                  hide-details="auto"
                  label="Ora fine"
                  placeholder="20:30"
                  class="mb-3"
                />
                <VSwitch
                  v-model="fillHeight"
                  density="comfortable"
                  hide-details="auto"
                  label="Fill parent height"
                  class="mb-3"
                />
                <VSelect
                  v-model="minHeight"
                  density="comfortable"
                  hide-details="auto"
                  label="Min height picker"
                  :items="[
                    { title: '240px', value: 240 },
                    { title: '320px', value: 320 },
                    { title: '420px', value: 420 }
                  ]"
                  item-title="title"
                  item-value="value"
                  class="mb-3"
                />
                <VSelect
                  v-model="density"
                  density="comfortable"
                  hide-details="auto"
                  label="Density (slot height)"
                  :items="[
                    { title: 'Default (56px)', value: 'default' },
                    { title: 'Comfortable (48px)', value: 'comfortable' },
                    { title: 'Compact (40px)', value: 'compact' }
                  ]"
                  item-title="title"
                  item-value="value"
                  class="mb-3"
                />
                <VSelect
                  v-model="componentWidth"
                  density="comfortable"
                  hide-details="auto"
                  label="Component width"
                  :items="[
                    { title: '328px (calendar)', value: '328' },
                    { title: '100%', value: '100%' },
                    { title: '480px', value: '480px' },
                    { title: '360px', value: '360px' }
                  ]"
                  item-title="title"
                  item-value="value"
                  class="mb-3"
                />
                <VSelect
                  v-model="layoutVariant"
                  density="comfortable"
                  hide-details="auto"
                  label="Layout variant"
                  :items="[
                    { title: 'Split (2 colonne)', value: 'split' },
                    { title: 'Stacked', value: 'stacked' }
                  ]"
                  item-title="title"
                  item-value="value"
                  class="mb-3"
                />
                <VSelect
                  v-model="surfaceVariant"
                  density="comfortable"
                  hide-details="auto"
                  label="Surface variant"
                  :items="[
                    { title: 'Transparent', value: 'transparent' },
                    { title: 'Outlined', value: 'outlined' }
                  ]"
                  item-title="title"
                  item-value="value"
                  class="mb-3"
                />
                <VSelect
                  v-model="preset"
                  density="comfortable"
                  hide-details="auto"
                  label="Preset slot disabilitati"
                  :items="[
                    { title: 'Nessuno', value: 'none' },
                    { title: 'Lunch break', value: 'lunch' },
                    { title: 'Morning block', value: 'morning' },
                    { title: 'Evening block', value: 'evening' }
                  ]"
                  item-title="title"
                  item-value="value"
                />
              </VWindowItem>

              <VWindowItem value="datepicker">
                <VCard
                  class="mb-4"
                  variant="tonal"
                >
                  <VCardTitle class="text-subtitle-2">
                    Colori
                  </VCardTitle>
                  <VCardText>
                    <VMenu
                      location="bottom"
                      :close-on-content-click="false"
                    >
                      <template #activator="{ props: menuProps }">
                        <VTextField
                          v-bind="menuProps"
                          v-model="dpColor"
                          density="comfortable"
                          hide-details="auto"
                          label="Color"
                          prepend-inner-icon="ri-palette-line"
                          class="mb-3"
                        />
                      </template>
                      <VCard>
                        <VColorPicker
                          v-model="dpColor"
                          show-swatches
                          mode="hex"
                        />
                      </VCard>
                    </VMenu>
                    <VMenu
                      location="bottom"
                      :close-on-content-click="false"
                    >
                      <template #activator="{ props: menuProps }">
                        <VTextField
                          v-bind="menuProps"
                          v-model="dpBgColor"
                          density="comfortable"
                          hide-details="auto"
                          label="Background color"
                          placeholder="#FFFFFF"
                          prepend-inner-icon="ri-palette-line"
                          class="mb-3"
                        />
                      </template>
                      <VCard>
                        <VColorPicker
                          v-model="dpBgColor"
                          show-swatches
                          mode="hex"
                        />
                        <VCardActions>
                          <VSpacer />
                          <VBtn
                            size="small"
                            variant="text"
                            @click="dpBgColor = ''"
                          >
                            Clear
                          </VBtn>
                        </VCardActions>
                      </VCard>
                    </VMenu>
                    <VMenu
                      location="bottom"
                      :close-on-content-click="false"
                    >
                      <template #activator="{ props: menuProps }">
                        <VTextField
                          v-bind="menuProps"
                          v-model="dpBaseColor"
                          density="comfortable"
                          hide-details="auto"
                          label="Base color"
                          placeholder="#9E9E9E"
                          prepend-inner-icon="ri-palette-line"
                        />
                      </template>
                      <VCard>
                        <VColorPicker
                          v-model="dpBaseColor"
                          show-swatches
                          mode="hex"
                        />
                        <VCardActions>
                          <VSpacer />
                          <VBtn
                            size="small"
                            variant="text"
                            @click="dpBaseColor = ''"
                          >
                            Clear
                          </VBtn>
                        </VCardActions>
                      </VCard>
                    </VMenu>
                  </VCardText>
                </VCard>
                <VSelect
                  v-model="dpViewMode"
                  density="comfortable"
                  hide-details="auto"
                  label="View mode"
                  :items="[
                    { title: 'Month', value: 'month' },
                    { title: 'Months', value: 'months' },
                    { title: 'Year', value: 'year' }
                  ]"
                  item-title="title"
                  item-value="value"
                  class="mb-3"
                />
                <VSelect
                  v-model="dpFirstDayOfWeek"
                  density="comfortable"
                  hide-details="auto"
                  label="First day of week"
                  :items="[
                    { title: 'Sunday (0)', value: 0 },
                    { title: 'Monday (1)', value: 1 },
                    { title: 'Saturday (6)', value: 6 }
                  ]"
                  item-title="title"
                  item-value="value"
                  class="mb-3"
                />
                <VSelect
                  v-model="dpElevation"
                  density="comfortable"
                  hide-details="auto"
                  label="Elevation"
                  :items="[
                    { title: '0', value: 0 },
                    { title: '2', value: 2 },
                    { title: '4', value: 4 },
                    { title: '8', value: 8 },
                    { title: '16', value: 16 },
                    { title: '24', value: 24 }
                  ]"
                  item-title="title"
                  item-value="value"
                  class="mb-3"
                />
                <VTextField
                  v-model="dpTitle"
                  density="comfortable"
                  hide-details="auto"
                  label="Title"
                  placeholder="SELECT DATE"
                  class="mb-3"
                />
                <VTextField
                  v-model="dpHeader"
                  density="comfortable"
                  hide-details="auto"
                  label="Header text"
                  placeholder="Custom header"
                  class="mb-3"
                />
                <VSelect
                  v-model="dpWeekdayFormat"
                  density="comfortable"
                  hide-details="auto"
                  label="Weekday format"
                  :items="[
                    { title: 'Narrow', value: 'narrow' },
                    { title: 'Short', value: 'short' },
                    { title: 'Long', value: 'long' }
                  ]"
                  item-title="title"
                  item-value="value"
                  class="mb-3"
                />
                <VSwitch
                  v-model="dpShowAdjacentMonths"
                  density="comfortable"
                  hide-details="auto"
                  label="Show adjacent months"
                  class="mb-3"
                />
                <VSwitch
                  v-model="dpHideHeader"
                  density="comfortable"
                  hide-details="auto"
                  label="Hide header"
                  class="mb-3"
                />
                <VSwitch
                  v-model="dpHideWeekdays"
                  density="comfortable"
                  hide-details="auto"
                  label="Hide weekdays"
                  class="mb-3"
                />
                <VSwitch
                  v-model="dpLandscape"
                  density="comfortable"
                  hide-details="auto"
                  label="Landscape"
                  class="mb-3"
                />
                <VSwitch
                  v-model="dpMultiple"
                  density="comfortable"
                  hide-details="auto"
                  label="Multiple dates"
                  class="mb-3"
                />
                <VSwitch
                  v-model="dpFlat"
                  density="comfortable"
                  hide-details="auto"
                  label="Flat"
                  class="mb-3"
                />
                <VSwitch
                  v-model="dpActive"
                  density="comfortable"
                  hide-details="auto"
                  label="Active"
                  class="mb-3"
                />
                <VSelect
                  v-model="dpAllowedDatesMode"
                  density="comfortable"
                  hide-details="auto"
                  label="Allowed dates"
                  :items="allowedDateModes"
                  item-title="title"
                  item-value="value"
                  class="mb-3"
                />
                <VTextField
                  v-model="dpMinDate"
                  density="comfortable"
                  hide-details="auto"
                  label="Min date (YYYY-MM-DD)"
                  placeholder="2026-01-01"
                  class="mb-3"
                />
                <VTextField
                  v-model="dpMaxDate"
                  density="comfortable"
                  hide-details="auto"
                  label="Max date (YYYY-MM-DD)"
                  placeholder="2026-12-31"
                />
              </VWindowItem>
            </VWindow>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        md="8"
      >
        <VCard
          variant="outlined"
          class="mb-4"
        >
          <VCardTitle class="text-subtitle-1">
            Preview
          </VCardTitle>
          <VCardText>
            <div class="preview-container">
              <DateTimePickerUnified
                v-bind="datePickerAttrs"
                :scheduled-at="combinedDateTimeLocal"
                date-label="Select date"
                time-label="Select time"
                :layout-variant="layoutVariant"
                :surface-variant="surfaceVariant"
                :panel-elevation="dpElevation"
                :panel-min-height="minHeight"
                :time-display="display"
                :time-layout="layout"
                :time-format="format"
                :time-step-minutes="stepMinutes"
                :time-start="startTime"
                :time-end="endTime"
                :time-width="componentWidth"
                :time-fill-height="fillHeight"
                :time-min-height="minHeight"
                :time-density="density"
                :get-disabled-slots="() => presetDisabledSlots.value"
                @update:selected-date="value => (selectedDate.value = value)"
                @update:selected-time="value => (selectedTime.value = value)"
              />
            </div>
          </VCardText>
        </VCard>

        <VCard variant="tonal">
          <VCardTitle class="text-subtitle-1">
            Output
          </VCardTitle>
          <VCardText>
            <VAlert
              class="mb-3"
              :type="combinedDateTimeIso ? 'success' : 'warning'"
              variant="tonal"
            >
              <div v-if="combinedDateTimeIso">
                <strong>Simulazione OK:</strong> data e ora combinate correttamente.
              </div>
              <div v-else>
                <strong>Simulazione incompleta:</strong> seleziona una data e uno slot orario valido.
              </div>
            </VAlert>
            <div class="mb-2">
              <strong>Date selezionata (raw):</strong> {{ selectedDate || 'none' }}
            </div>
            <div class="mb-2">
              <strong>Date selezionata (YYYY-MM-DD):</strong> {{ selectedDateOnly || 'none' }}
            </div>
            <div class="mb-2">
              <strong>Time selezionato:</strong> {{ selectedTime || 'none' }}
            </div>
            <div class="mb-2">
              <strong>DateTime locale:</strong> {{ combinedDateTimeLocal || 'none' }}
            </div>
            <div class="mb-4">
              <strong>DateTime ISO:</strong> {{ combinedDateTimeIso || 'none' }}
            </div>
            <VCard
              class="mb-4"
              variant="outlined"
            >
              <VCardTitle class="text-subtitle-2">
                Preview formato Laravel
              </VCardTitle>
              <VCardText>
                <div class="mb-2">
                  <strong>Formato atteso:</strong> <code>Y-m-d\\TH:i:sP</code>
                </div>
                <div>
                  <strong>Valore:</strong> {{ combinedDateTimeLaravel || 'none' }}
                </div>
              </VCardText>
            </VCard>
            <VCard
              class="mb-4"
              variant="outlined"
            >
              <VCardTitle class="text-subtitle-2">
                Display formattato
              </VCardTitle>
              <VCardText>
                <div class="mb-2">
                  <strong>Italiano:</strong> {{ combinedDateTimeFormattedIt || 'none' }}
                </div>
                <div>
                  <strong>English (UK):</strong> {{ combinedDateTimeFormattedEn || 'none' }}
                </div>
              </VCardText>
            </VCard>
            <pre class="demo-output mb-4">{{ JSON.stringify(simulationPayload, null, 2) }}</pre>
            <pre class="demo-output">{{ JSON.stringify(configSnapshot, null, 2) }}</pre>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </VContainer>
</template>

<style scoped>
.demo-output {
  background: rgba(var(--v-theme-on-surface), 0.04);
  border-radius: 8px;
  padding: 12px;
  overflow: auto;
}

.preview-container {
  width: 100%;
}

.control-card {
  height: calc(100vh - 190px);
  min-height: 720px;
}

.control-scroll {
  max-height: calc(100vh - 280px);
  overflow-y: auto;
  padding-right: 8px;
}
</style>
